import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["header", "resizer"]
  
  connect() {
    this.isResizing = false
    this.currentResizer = null
    this.startX = 0
    this.startWidth = 0
    
    this.setupResizers()
    this.loadColumnWidths()
  }
  
  disconnect() {
    this.removeResizers()
  }
  
  setupResizers() {
    // Remove any existing resizers
    this.removeResizers()
    
    // Add resizers to each header cell except the last one
    this.headerTargets.forEach((header, index) => {
      if (index < this.headerTargets.length - 1) { // Don't add resizer to last column
        const resizer = document.createElement('div')
        resizer.className = 'column-resizer'
        resizer.setAttribute('data-resizable-table-target', 'resizer')
        resizer.setAttribute('data-action', 'mousedown->resizable-table#startResize')
        resizer.setAttribute('data-column-index', index)
        
        header.appendChild(resizer)
      }
    })
  }
  
  removeResizers() {
    this.resizerTargets.forEach(resizer => {
      resizer.remove()
    })
  }
  
  startResize(event) {
    event.preventDefault()
    
    this.isResizing = true
    this.currentResizer = event.target
    this.startX = event.pageX
    
    const header = this.currentResizer.parentElement
    this.startWidth = header.offsetWidth
    
    // Add event listeners for mouse move and mouse up
    document.addEventListener('mousemove', this.boundMouseMove = this.mouseMove.bind(this))
    document.addEventListener('mouseup', this.boundMouseUp = this.mouseUp.bind(this))
    
    // Add visual feedback
    document.body.style.cursor = 'col-resize'
    this.currentResizer.classList.add('resizing')
  }
  
  mouseMove(event) {
    if (!this.isResizing) return
    
    const deltaX = event.pageX - this.startX
    const newWidth = Math.max(50, this.startWidth + deltaX) // Minimum width of 50px
    
    const header = this.currentResizer.parentElement
    header.style.width = `${newWidth}px`
    header.style.minWidth = `${newWidth}px`
    
    // Update all cells in this column
    const columnIndex = parseInt(this.currentResizer.getAttribute('data-column-index'))
    this.updateColumnWidth(columnIndex, newWidth)
  }
  
  mouseUp(event) {
    if (!this.isResizing) return
    
    this.isResizing = false
    
    // Remove event listeners
    document.removeEventListener('mousemove', this.boundMouseMove)
    document.removeEventListener('mouseup', this.boundMouseUp)
    
    // Remove visual feedback
    document.body.style.cursor = ''
    this.currentResizer.classList.remove('resizing')
    
    // Store the width in localStorage for persistence
    const columnIndex = parseInt(this.currentResizer.getAttribute('data-column-index'))
    const header = this.currentResizer.parentElement
    const width = header.offsetWidth
    this.saveColumnWidth(columnIndex, width)
    
    this.currentResizer = null
  }
  
  updateColumnWidth(columnIndex, width) {
    const table = this.element.querySelector('table')
    const rows = table.querySelectorAll('tr')
    
    rows.forEach(row => {
      const cell = row.cells[columnIndex]
      if (cell) {
        if (width === null) {
          cell.style.width = ''
          cell.style.minWidth = ''
        } else {
          cell.style.width = `${width}px`
          cell.style.minWidth = `${width}px`
        }
      }
    })
  }
  
  saveColumnWidth(columnIndex, width) {
    const tableId = this.element.getAttribute('data-table-id') || 'default'
    const key = `table_${tableId}_column_${columnIndex}_width`
    localStorage.setItem(key, width.toString())
  }
  
  loadColumnWidths() {
    const tableId = this.element.getAttribute('data-table-id') || 'default'
    
    this.headerTargets.forEach((header, index) => {
      const key = `table_${tableId}_column_${index}_width`
      const savedWidth = localStorage.getItem(key)
      
      if (savedWidth) {
        const width = parseInt(savedWidth)
        header.style.width = `${width}px`
        header.style.minWidth = `${width}px`
        this.updateColumnWidth(index, width)
      }
    })
  }
  
  resetColumnWidths() {
    const tableId = this.element.getAttribute('data-table-id') || 'default'
    
    // Clear saved widths from localStorage
    this.headerTargets.forEach((header, index) => {
      const key = `table_${tableId}_column_${index}_width`
      localStorage.removeItem(key)
      
      // Reset header styles
      header.style.width = ''
      header.style.minWidth = ''
      
      // Reset all cells in this column
      this.updateColumnWidth(index, null)
    })
  }
} 